import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  ArrowRight,
  Instagram,
  Linkedin,
  Twitter,
  Facebook,
  Mail,
  MapPin,
  Phone,
  Sparkles,
} from "lucide-react";

import { zulfalFooterLogo } from "../../../assets";
import WhatsAppButton from "../../chat-us/whatsApp";

const phoneNumber = process.env.REACT_APP_WHATSAPP_NUMBER;

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <>
      {/* FLOATING WHATSAPP */}
      <WhatsAppButton
        phoneNumber={phoneNumber}
        className="fixed bottom-7 right-5 z-[100]"
        message="Hello, I would like to know more about Zulfah Academy."
      />

      <footer className="relative overflow-hidden bg-[#070809] text-white">

        {/* =====================================================
            AMBIENT BACKGROUND
        ====================================================== */}

        <div className="pointer-events-none absolute inset-0 overflow-hidden">

          {/* cyan glow */}
          <motion.div
            animate={{
              x: [0, 100, 0],
              y: [0, -60, 0],
              scale: [1, 1.15, 1],
            }}
            transition={{
              duration: 14,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -left-40 -top-40 h-[600px] w-[600px] rounded-full bg-[#59b5cd]/10 blur-[150px]"
          />

          {/* yellow glow */}
          <motion.div
            animate={{
              x: [0, -80, 0],
              y: [0, 50, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 16,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -bottom-60 -right-40 h-[600px] w-[600px] rounded-full bg-[#ecb32c]/10 blur-[160px]"
          />

          {/* subtle center glow */}
          <div className="absolute left-1/2 top-1/2 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#59b5cd]/[0.025] blur-[120px]" />

          {/* grid */}
          <div
            className="absolute inset-0 opacity-[0.025]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.8) 1px, transparent 1px)",
              backgroundSize: "70px 70px",
            }}
          />

        </div>


        <div className="relative mx-auto max-w-[1500px] px-6 sm:px-8 md:px-12 lg:px-20">


          {/* =====================================================
              MASSIVE CTA
          ====================================================== */}

          <section className="relative border-b border-white/10 py-28 md:py-36 lg:py-44">

            <div className="grid gap-14 lg:grid-cols-[1.3fr_0.7fr] lg:items-end">

              <div>

                <div className="mb-8 flex items-center gap-3">

                  <span className="h-2 w-2 animate-pulse rounded-full bg-[#59b5cd]" />

                  <span className="text-[9px] uppercase tracking-[0.4em] text-white/40">
                    Start your journey
                  </span>

                </div>

                <h2 className="max-w-6xl font-[ubuntu] text-[58px] font-semibold leading-[0.82] tracking-[-0.075em] sm:text-7xl md:text-8xl lg:text-[9rem]">

                  Your future
                  <br />

                  <span className="text-white/20">
                    starts here.
                  </span>

                </h2>

              </div>


              <div className="lg:pb-3">

                <p className="max-w-md text-sm leading-7 text-white/45 md:text-base">
                  Learn practical technology skills, build real projects and
                  develop the confidence to create your place in the digital
                  economy.
                </p>

                <Link
                  to="/courses"
                  className="group mt-8 inline-flex items-center gap-4 rounded-full bg-white px-7 py-4 text-[10px] font-semibold uppercase tracking-[0.2em] text-black transition duration-300 hover:-translate-y-1 hover:bg-[#59b5cd] hover:text-white"
                >
                  Explore our courses

                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-black text-white transition group-hover:bg-white group-hover:text-black">

                    <ArrowUpRight className="h-3.5 w-3.5 transition group-hover:rotate-45" />

                  </span>

                </Link>

              </div>

            </div>


            {/* floating decorative text */}

            <div className="pointer-events-none absolute bottom-[-30px] right-0 hidden select-none font-[ubuntu] text-[16vw] font-bold leading-none tracking-[-0.1em] text-white/[0.018] lg:block">
              ZULFAH
            </div>

          </section>


          {/* =====================================================
              BRAND + NAVIGATION
          ====================================================== */}

          <section className="grid gap-16 border-b border-white/10 py-20 md:grid-cols-2 lg:grid-cols-[1.4fr_0.7fr_0.7fr_0.9fr] lg:py-24">


            {/* BRAND */}

            <div>

              <Link to="/" className="inline-block">

                <motion.div
                  whileHover={{ scale: 1.04 }}
                  className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white p-2 shadow-2xl shadow-black/20"
                >
                  <img
                    src={zulfalFooterLogo}
                    alt="Zulfah Academy"
                    className="h-full w-full object-contain"
                  />
                </motion.div>

              </Link>


              <h3 className="mt-8 max-w-sm font-[ubuntu] text-3xl font-semibold leading-[0.95] tracking-[-0.045em]">
                Building people who can
                <span className="text-[#59b5cd]">
                  {" "}build the future.
                </span>
              </h3>


              <p className="mt-6 max-w-sm text-sm leading-7 text-white/35">
                Zulfah Academy equips individuals and organisations with
                practical technology skills for the real world.
              </p>


              {/* SOCIALS */}

              <div className="mt-8 flex gap-2">

                {[
                  {
                    icon: Facebook,
                    url: "https://web.facebook.com/profile.php?id=100076343703349",
                    label: "Facebook",
                  },
                  {
                    icon: Twitter,
                    url: "https://x.com/@zulfahng",
                    label: "X",
                  },
                  {
                    icon: Linkedin,
                    url: "https://www.linkedin.com/company/zulfah-group/",
                    label: "LinkedIn",
                  },
                  {
                    icon: Instagram,
                    url: "https://www.instagram.com/zulfahng/",
                    label: "Instagram",
                  },
                ].map(({ icon: Icon, url, label }) => (

                  <a
                    key={label}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-white/40 transition duration-300 hover:-translate-y-1 hover:border-[#59b5cd] hover:bg-[#59b5cd] hover:text-white"
                  >
                    <Icon className="h-4 w-4" />
                  </a>

                ))}

              </div>

            </div>


            {/* EXPLORE */}

            <div>

              <span className="text-[9px] uppercase tracking-[0.35em] text-white/25">
                Explore
              </span>

              <div className="mt-7 flex flex-col gap-4">

                {[
                  ["Home", "/"],
                  ["About us", "/about-us"],
                  ["Courses", "/courses"],
                  ["Events", "/events"],
                  ["Contact", "/contact-us"],
                ].map(([name, url]) => (

                  <Link
                    key={name}
                    to={url}
                    className="group flex items-center gap-2 text-sm text-white/45 transition hover:text-white"
                  >
                    <span>{name}</span>

                    <ArrowRight className="h-3.5 w-3.5 -translate-x-2 opacity-0 transition group-hover:translate-x-0 group-hover:opacity-100" />
                  </Link>

                ))}

              </div>

            </div>


            {/* LEARN */}

            <div>

              <span className="text-[9px] uppercase tracking-[0.35em] text-white/25">
                Learn
              </span>

              <div className="mt-7 flex flex-col gap-4">

                {[
                  "Software Engineering",
                  "Cybersecurity",
                  "Data Engineering",
                  "Artificial Intelligence",
                  "Cloud Computing",
                ].map((course) => (

                  <Link
                    key={course}
                    to="/courses"
                    className="group flex items-center gap-2 text-sm text-white/45 transition hover:text-white"
                  >

                    <span>{course}</span>

                    <ArrowRight className="h-3.5 w-3.5 -translate-x-2 opacity-0 transition group-hover:translate-x-0 group-hover:opacity-100" />

                  </Link>

                ))}

              </div>

            </div>


            {/* CONTACT */}

            <div>

              <span className="text-[9px] uppercase tracking-[0.35em] text-white/25">
                Connect
              </span>

              <div className="mt-7 space-y-6">

                <a
                  href={`https://wa.me/2348115371014?text=Hello`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex gap-3"
                >

                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/5">
                    <Phone className="h-4 w-4 text-[#59b5cd]" />
                  </div>

                  <div>
                    <span className="block text-[9px] uppercase tracking-[0.2em] text-white/20">
                      WhatsApp
                    </span>

                    <span className="mt-1 block text-sm text-white/60 transition group-hover:text-white">
                      Talk to us
                    </span>
                  </div>

                </a>


                <a
                  href="mailto:info@zulfahgroup.com"
                  className="group flex gap-3"
                >

                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/5">
                    <Mail className="h-4 w-4 text-[#ecb32c]" />
                  </div>

                  <div>
                    <span className="block text-[9px] uppercase tracking-[0.2em] text-white/20">
                      Email
                    </span>

                    <span className="mt-1 block break-all text-sm text-white/60 transition group-hover:text-white">
                      info@zulfahgroup.com
                    </span>
                  </div>

                </a>


                <div className="flex gap-3">

                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/5">
                    <MapPin className="h-4 w-4 text-[#59b5cd]" />
                  </div>

                  <div>
                    <span className="block text-[9px] uppercase tracking-[0.2em] text-white/20">
                      Location
                    </span>

                    <span className="mt-1 block text-sm text-white/60">
                      Lagos, Nigeria
                    </span>
                  </div>

                </div>

              </div>

            </div>

          </section>


          {/* =====================================================
              FEATURE STRIP
          ====================================================== */}

          <section className="border-b border-white/10 py-10">

            <div className="flex flex-col gap-8 md:flex-row md:items-center md:justify-between">

              <div className="flex items-center gap-3">

                <Sparkles className="h-4 w-4 text-[#ecb32c]" />

                <span className="text-[10px] uppercase tracking-[0.25em] text-white/30">
                  Learn · Build · Transform
                </span>

              </div>


              <div className="flex flex-wrap gap-x-8 gap-y-3 text-[9px] uppercase tracking-[0.2em] text-white/20">

                <span>Practical Learning</span>
                <span>Industry Skills</span>
                <span>Real Projects</span>
                <span>Career Growth</span>

              </div>

            </div>

          </section>


          {/* =====================================================
              BOTTOM BAR
          ====================================================== */}

          <section className="flex flex-col gap-5 py-8 md:flex-row md:items-center md:justify-between">

            <p className="text-[10px] uppercase tracking-[0.18em] text-white/25">
              © {currentYear} Zulfah Academy. All rights reserved.
            </p>


            <div className="flex flex-wrap gap-6">

              <Link
                to="/privacy-policy"
                className="text-[10px] uppercase tracking-[0.15em] text-white/25 transition hover:text-white"
              >
                Privacy
              </Link>

              <Link
                to="/terms-of-service"
                className="text-[10px] uppercase tracking-[0.15em] text-white/25 transition hover:text-white"
              >
                Terms
              </Link>

              <Link
                to="/cookies-policy"
                className="text-[10px] uppercase tracking-[0.15em] text-white/25 transition hover:text-white"
              >
                Cookies
              </Link>

            </div>

          </section>

        </div>

      </footer>
    </>
  );
};

export default Footer;